import enLang from './entries/en-US'
import { LocaleEnum } from '../types/i18n'

// Antd locale imports - these are small and needed for UI components
import antdFr from 'antd/locale/fr_FR'
import antdEs from 'antd/locale/es_ES'
import antdPt from 'antd/locale/pt_BR'
import antdDe from 'antd/locale/de_DE'
import antdIt from 'antd/locale/it_IT'
import antdPl from 'antd/locale/pl_PL'
import antdTr from 'antd/locale/tr_TR'
import antdSv from 'antd/locale/sv_SE'

// Type for locale entry
export interface ILocaleEntry {
  messages: Record<string, string>
  antd: typeof enLang.antd
  locale: string
}

// Antd locales map (these are small, so we bundle them)
const antdLocales = {
  [LocaleEnum.fr]: antdFr,
  [LocaleEnum.es]: antdEs,
  [LocaleEnum.pt]: antdPt,
  [LocaleEnum.de]: antdDe,
  [LocaleEnum.it]: antdIt,
  [LocaleEnum.pl]: antdPl,
  [LocaleEnum.tr]: antdTr,
  [LocaleEnum.sv]: antdSv,
}

// Locale to JSON filename mapping
const localeFileMap: Record<LocaleEnum, string> = {
  [LocaleEnum.en]: 'en-us',
  [LocaleEnum.fr]: 'fr-fr',
  [LocaleEnum.es]: 'es-es',
  [LocaleEnum.pt]: 'pt-br',
  [LocaleEnum.de]: 'de-de',
  [LocaleEnum.it]: 'it-it',
  [LocaleEnum.pl]: 'pl-pl',
  [LocaleEnum.tr]: 'tr-tr',
  [LocaleEnum.sv]: 'sv-se',
}

// Locale to full locale string mapping
const localeStringMap: Record<LocaleEnum, string> = {
  [LocaleEnum.en]: 'en-US',
  [LocaleEnum.fr]: 'fr-FR',
  [LocaleEnum.es]: 'es-ES',
  [LocaleEnum.pt]: 'pt-BR',
  [LocaleEnum.de]: 'de-DE',
  [LocaleEnum.it]: 'it-IT',
  [LocaleEnum.pl]: 'pl-PL',
  [LocaleEnum.tr]: 'tr-TR',
  [LocaleEnum.sv]: 'sv-SE',
}

// Cache for loaded language messages
const languageCache: Partial<Record<LocaleEnum, ILocaleEntry>> = {
  [LocaleEnum.en]: enLang, // English is always bundled
}

// Build version for cache busting (injected at build time, fallback for dev/tests)
const buildVersion = typeof process !== 'undefined' && process.env?.BUILD_VERSION ? process.env.BUILD_VERSION : 'dev'

/**
 * Load language messages dynamically from /locales/{locale}.json
 * Returns cached entry if already loaded
 */
export async function loadLanguage(locale: LocaleEnum): Promise<ILocaleEntry> {
  // Return cached if available
  const cached = languageCache[locale]
  if (cached) {
    return cached
  }

  // English is always bundled
  if (locale === LocaleEnum.en) {
    return enLang
  }

  try {
    const fileName = localeFileMap[locale]
    const response = await fetch(`/locales/${fileName}.json?v=${buildVersion}`)

    if (!response.ok) {
      return enLang
    }

    const messages = await response.json()

    const entry: ILocaleEntry = {
      messages,
      antd: antdLocales[locale] || enLang.antd,
      locale: localeStringMap[locale],
    }

    // Cache for future use
    languageCache[locale] = entry
    return entry
  } catch {
    // Fallback to English on any error
    return enLang
  }
}

/**
 * Get cached language entry (synchronous)
 * Returns English if the requested locale is not cached
 */
export function getCachedLanguage(locale: LocaleEnum): ILocaleEntry {
  return languageCache[locale] || enLang
}

/**
 * Check if a language is already loaded/cached
 */
export function isLanguageLoaded(locale: LocaleEnum): boolean {
  return locale in languageCache
}

// Default export for backward compatibility - only contains English now
// Other languages should be loaded via loadLanguage()
const AppLocale: Record<LocaleEnum, ILocaleEntry> = {
  [LocaleEnum.en]: enLang,
  // These are placeholders that return English - use loadLanguage() for actual translations
  [LocaleEnum.fr]: enLang,
  [LocaleEnum.es]: enLang,
  [LocaleEnum.pt]: enLang,
  [LocaleEnum.de]: enLang,
  [LocaleEnum.it]: enLang,
  [LocaleEnum.pl]: enLang,
  [LocaleEnum.tr]: enLang,
  [LocaleEnum.sv]: enLang,
}

export default AppLocale
